import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { ThemeApiService } from "@/services/themeApiService";
import type { Theme } from "@/models/theme";
import type { ThemeDefinition } from "vuetify";
import { useFavicon } from "@vueuse/core";

export type ThemeHeroSlide = {
  image: string;
  superTitle?: string;
  superTitleCss?: string;
  title: string;
  titleCss?: string;
  subTitle?: string;
  subTitleCss?: string;
  buttonLabel: string;
  buttonLink: string;
  buttonCss?: string;
};

const DEFAULT_THEME: ThemeDefinition = {
  dark: false,
  colors: {
    background: '#FFFFFF',
    surface: '#FFFFFF',
    primary: '#0099EB',
    'primary-300': '#003452',
    'primary-darken-1': '#003452',
    secondary: '#A8D100',
    'secondary-100': '#CFED49',
    'secondary-200': '#A8D100',
    'secondary-darken-1': '#4E6004',
    'neutral-100': "#F4F7FE",
    'neutral-200': "#E4E8F0",
    'neutral-300': "#CCD7E1",
    'neutral-400': "#5D7189",
    'neutral-500': "#26374D",
    'neutral-600': "#0F1B32",
    'status-red-darken-2': "#D32F2F",
    error: '#B00020',
    'error-100': '#F7D6D6',
    'error-300': '#D32F2F',
    'error-400': '#B71C1C',
    info: '#2196F3',
    success: '#27AE60',
    'success-100': '#D3EFDE',
    'success-200': '#27AE60',
    'success-300': '#17683A',
    warning: '#FB8C00',
    'warning-100': '#FFF9C4',
    'warning-300': '#FDD835',
    'warning-400': '#F9A825',
    'blue-100': '#DDF3FF',
    'blue-200': '#A8E1FF',
    'blue-300': '#5DBCF4',
    'blue-400': '#0093D0',
    'blue-500': '#025085',
    'yellow-lighten-4': '#FFF9C4',
  },
};

export const useThemeStore = defineStore('theme', () => {
  // State
  const themeLoaded = ref<boolean>(false);
  const apiTheme = ref<Theme | undefined>(undefined);
  const themeHeroSlides = ref<ThemeHeroSlide[]>([]);

  const logo = computed(() => apiTheme.value?.images.find(i => i.key == 'logo')?.imagePath);
  const favicon = computed(() => apiTheme.value?.images.find(i => i.key == 'favicon')?.imagePath);

  const referralBg = computed(() => apiTheme.value?.images.find(i => i.key == 'referral_bg')?.imagePath);

  // Getters
  function isThemeLoaded() {
    return themeLoaded.value;
  }

  function getTheme(): Theme | undefined {
    return apiTheme.value;
  }

  function getThemeDefinition(): ThemeDefinition {

    const buffer = DEFAULT_THEME;

    // Override default theme with values from API
    if (apiTheme.value) {
      // @ts-ignore
      for(const [key, value] of Object.entries(apiTheme.value.settings)) {
        const kebabKey = key.replace(/([a-z|1-9])([A-Z|1-9])/g, "$1-$2").toLowerCase();
        (buffer.colors ?? {})[kebabKey] = value;
      }
    }

    return buffer;
  }

  // Actions
  async function fetchTheme() {
    if (themeLoaded.value) {
      return;
    }

    const response = await ThemeApiService.get();
    apiTheme.value = response?.data?.theme;
    themeLoaded.value = true;

    // Set favicon
    if (favicon.value) {
      useFavicon(favicon.value);
    }

    // Build hero slides
    // Images
    for (const image of apiTheme.value?.images ?? []) {
      buildThemeHeroSlides(image);
    }

    // Labels
    for (const label of apiTheme.value?.labels ?? []) {
      buildThemeHeroSlides(label);
    }

    // Anchors
    for (const anchor of apiTheme.value?.anchors ?? []) {
      buildThemeHeroSlides(anchor);
    }
    themeHeroSlides.value = Object.values(themeHeroSlides.value);
  }

  // Build theme hero slides
  function buildThemeHeroSlides(themeItem: any) {

    if (!themeItem.key.startsWith('hero_slide')) {
      return;
    }

    const parts = themeItem.key.split('_');
    const index = parseInt(parts[2]);

    const element = parts[3];

    if (themeHeroSlides.value[index] == undefined) {
      themeHeroSlides.value[index] = {
        image: '',
        superTitle: '',
        superTitleCss: '',
        title: '',
        titleCss: '',
        subTitle: '',
        subTitleCss: '',
        buttonLabel: '',
        buttonLink: '',
        buttonCss: '',
      }
    }

    switch (element) {
      case 'image':
        themeHeroSlides.value[index].image = themeItem.imagePath;
        break;
      case 'superTitle':
        themeHeroSlides.value[index].superTitle = themeItem.value;
        themeHeroSlides.value[index].superTitleCss = themeItem.css;
        break;
      case 'title':
        themeHeroSlides.value[index].title = themeItem.value;
        themeHeroSlides.value[index].titleCss = themeItem.css;
        break;
      case 'subTitle':
        themeHeroSlides.value[index].subTitle = themeItem.value;
        themeHeroSlides.value[index].subTitleCss = themeItem.css;
        break;
      case 'button':
        themeHeroSlides.value[index].buttonLabel = themeItem.value;
        themeHeroSlides.value[index].buttonLink = themeItem.href;
        themeHeroSlides.value[index].buttonCss = themeItem.css;
        break;
    }
  }

  function $reset() {
    // Stub
  }

  return {
    logo,
    favicon,
    referralBg,
    fetchTheme,
    isThemeLoaded,
    getTheme,
    getThemeDefinition,
    themeHeroSlides,
    $reset
  }
});
